import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * Native replacement for the `$rootScope.$broadcast`/`$on` channel between NATIVE
 * components (WS5 #10 Stage A inc 2i; full event census: ENDGAME-PLAN.md #106).
 * Delivery is synchronous (Subject.next, same call stack) — identical timing to an
 * in-zone `$rootScope.$broadcast`. `on()` returns a deregistration function (the
 * `$on` contract); the handler receives the PAYLOAD directly (no leading AngularJS
 * event object — call sites were adjusted with the move).
 *
 * Scope: native↔native events only. Cross-boundary events (one side still AngularJS:
 * `touchpoint.statusChanged` / `campaign.statusChanged`) stay on `$rootScope` until
 * their AngularJS side dies in the Stage B boot-stop.
 * WS5 #10 (BroadcastModal port): `[StoryModal].RecommendationComplete` moved here
 * (emitters = the native broadcast flows + ft-recommendation-notification; listener =
 * the native story modal) and the angularjs.module.ts run-block event bridge is GONE.
 */
@Injectable({ providedIn: 'root' })
export class EventBusService {
  private readonly events$ = new Subject<{ name: string; payload: any }>();

  emit(name: string, payload?: any): void {
    this.events$.next({ name, payload });
  }

  /** Subscribe to a named event; returns the deregistration function (the `$on` contract). */
  on(name: string, handler: (payload: any) => void): () => void {
    const sub = this.events$.pipe(filter((e) => e.name === name)).subscribe((e) => handler(e.payload));
    return () => sub.unsubscribe();
  }
}
